import React from "react";
import {
  FIELD_TYPE,
  OVERRIDE_DECISIONS,
  SIMPLE_DECISIONS,
  FieldDecision,
  FormConfigField,
  WorkspaceTask,
} from "../shared/types";
import * as S from "../shared/styles";
import { buildAiValues, buildHumanValues } from "./mapTaskValues";
import {
  MULTI_VALUE_SEPARATOR,
  OTHER_LABEL,
  correctionOptions,
  hasCorrectionValue,
  isCorrectionNoteMissing,
  isOptionField,
  isOtherValue,
  otherText,
  toOtherValue,
} from "../shared/helpers";

interface FieldReviewTableProps {
  fields: FormConfigField[];
  isOverride: boolean;
  task: WorkspaceTask;
  decisions: Record<string, FieldDecision>;
  onDecide: (label: string, patch: Partial<FieldDecision>) => void;
  showError: boolean;
  disabled?: boolean;
}

const FieldReviewTable: React.FC<FieldReviewTableProps> = ({
  fields,
  isOverride,
  task,
  decisions,
  onDecide,
  showError,
  disabled = false,
}) => {
  const decisionOptions = isOverride
    ? OVERRIDE_DECISIONS.map((v) => ({
        value: v,
        label:
          v === "ai_correct"
            ? "AI Correct"
            : v === "human_correct"
              ? "Human Correct"
              : "Both Incorrect",
      }))
    : SIMPLE_DECISIONS.map((v) => ({
        value: v,
        label: v === "correct" ? "Correct" : "Incorrect",
      }));

  const question = task.inspection_question?.trim() || null;
  const aiValues = React.useMemo(() => buildAiValues(task, fields), [task, fields]);
  const humanValues = React.useMemo(() => buildHumanValues(task, fields), [task, fields]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0px" }}>
      {question && <div style={questionBanner}>{question}</div>}
      {fields.map((f) => {
        const d = decisions[f.label] || {};
        const needsCorrection = d.decision === "incorrect" || d.decision === "both_incorrect";
        const missingDecision = f.mandatory && !d.decision;
        const missingCorrection = needsCorrection && !hasCorrectionValue(f, d.correction_value);
        const missingNote =
          needsCorrection && isCorrectionNoteMissing(f, d.correction_value, d.correction_note);
        const noteError = showError && missingNote;
        const hasError = showError && (missingDecision || missingCorrection || missingNote);

        return (
          <React.Fragment key={f.id}>
            <div style={{ ...fieldCard, ...(hasError ? { borderColor: "#fca5a5" } : {}) }}>
              {/* -- Row 1: field label -- */}
              <div style={fieldHeader}>
                <span style={{ ...fieldLabel, color: hasError ? "#ef4444" : "#374151" }}>
                  {f.label}
                  {f.mandatory && <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>}
                </span>
              </div>

              {/* -- Row 2: AI value (+ human value in override mode) -- */}
              {f.type === FIELD_TYPE.CHECKBOX ? (
                <CheckboxValue
                  label={f.options[0]?.trim() || f.label}
                  checked={String(aiValues[f.label] ?? "").toLowerCase() === "true"}
                />
              ) : (
                <div style={valuesRow}>
                  <div style={valueBlock}>
                    <div style={valueBox}>{aiValues[f.label] ?? "\u2014"}</div>
                  </div>
                  {isOverride && (
                    <div style={valueBlock}>
                      <div style={{ ...valueLabel, color: "#b45309" }}>Austin output</div>
                      <div style={{ ...valueBox, background: "#fef3c7", borderColor: "#fde68a" }}>
                        {humanValues[f.label] ?? "\u2014"}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* -- Row 3: Review decision -- */}
              <div style={decisionRow}>
                <div style={decisionLabel}>Review decision</div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {decisionOptions.map((o) => {
                    const selected = d.decision === o.value;
                    return (
                      <label
                        key={o.value}
                        style={{
                          ...pillLabel,
                          ...(selected ? pillSelected : pillDefault),
                          ...(disabled ? { cursor: "default", opacity: 0.7 } : {}),
                          ...(missingDecision && showError && !selected
                            ? { borderColor: "#fca5a5" }
                            : {}),
                        }}
                      >
                        <input
                          type="radio"
                          name={`decision-${f.id}`}
                          value={o.value}
                          checked={selected}
                          disabled={disabled}
                          onChange={() => {
                            const nextNeedsCorrection =
                              o.value === "incorrect" || o.value === "both_incorrect";
                            onDecide(
                              f.label,
                              nextNeedsCorrection
                                ? { decision: o.value }
                                : {
                                    decision: o.value,
                                    correction_value: undefined,
                                    correction_note: undefined,
                                    flagged: undefined,
                                  },
                            );
                          }}
                          style={{ display: "none" }}
                        />
                        {o.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* -- Row 4: Correction (only when Incorrect) -- */}
              {needsCorrection && (
                <div style={correctionSection}>
                  <CorrectionInput
                    field={f}
                    value={d.correction_value ?? ""}
                    disabled={disabled}
                    hasError={missingCorrection && showError}
                    onChange={(v) =>
                      // Clearing the value hides Details, so drop its note too.
                      onDecide(f.label, {
                        correction_value: v,
                        ...(v ? {} : { correction_note: undefined }),
                      })
                    }
                  />

                  {/* Dropdown corrections also require free-text detail once a value is picked */}
                  {isOptionField(f) && !!d.correction_value && (
                    <div>
                      <div style={{ ...noteLabel, ...(noteError ? { color: "#ef4444" } : {}) }}>
                        Details<span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
                      </div>
                      <textarea
                        value={d.correction_note ?? ""}
                        disabled={disabled}
                        onChange={(e) => onDecide(f.label, { correction_note: e.target.value })}
                        placeholder="Explain the evidence and the correct interpretation"
                        rows={2}
                        style={{
                          ...S.formInput,
                          fontSize: "1.4rem",
                          resize: "vertical" as const,
                          minHeight: "48px",
                          ...(noteError ? { borderColor: "#ef4444" } : {}),
                        }}
                      />
                    </div>
                  )}

                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "2px 0",
                      cursor: disabled ? "default" : "pointer",
                      fontSize: "1.3rem",
                      color: "#374151",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!d.flagged}
                      disabled={disabled}
                      onChange={(e) => onDecide(f.label, { flagged: e.target.checked })}
                      style={{ width: "15px", height: "15px", accentColor: "#0f7b8a" }}
                    />
                    Flag this record for data or guideline follow-up
                  </label>
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ----------------------------  CorrectionInput  ----------------------------

interface CorrectionInputProps {
  field: FormConfigField;
  value: string;
  disabled: boolean;
  hasError: boolean;
  onChange: (value: string) => void;
}

const CorrectionInput: React.FC<CorrectionInputProps> = ({
  field,
  value,
  disabled,
  hasError,
  onChange,
}) => {
  const errBorder = hasError ? "#ef4444" : "#d1d5db";
  const errColor = hasError ? "#ef4444" : "#6b7280";

  switch (field.type) {
    // -- Dropdown single: radio buttons from field.options --
    case FIELD_TYPE.DROPDOWN_SINGLE: {
      const opts = correctionOptions(field);
      const otherOn = !!value && isOtherValue(value);
      return (
        <fieldset
          style={{
            border: `1px solid ${errBorder}`,
            borderRadius: "6px",
            padding: "8px 10px",
            margin: 0,
          }}
        >
          <legend
            style={{ fontSize: "1.2rem", fontWeight: 600, color: errColor, padding: "0 4px" }}
          >
            Issue Category
            <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
          </legend>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {opts.map((o) => (
              <label
                key={o}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "6px",
                  padding: "3px 0",
                  fontSize: "1.3rem",
                  color: "#374151",
                  cursor: disabled ? "default" : "pointer",
                  lineHeight: 1.4,
                }}
              >
                <input
                  type="radio"
                  name={`correction-${field.id}`}
                  value={o}
                  checked={value === o}
                  disabled={disabled}
                  onChange={() => onChange(o)}
                  style={{ marginTop: "3px", flexShrink: 0 }}
                />
                <span style={{ wordBreak: "break-word" }}>{o}</span>
              </label>
            ))}
            {field.allow_other && (
              <OtherOption
                type="radio"
                name={`correction-${field.id}`}
                checked={otherOn}
                text={otherOn ? otherText(value) : ""}
                disabled={disabled}
                hasError={hasError}
                onToggle={() => onChange(OTHER_LABEL)}
                onText={(t) => onChange(toOtherValue(t))}
              />
            )}
          </div>
        </fieldset>
      );
    }

    // -- Dropdown multi: checkboxes from field.options --
    case FIELD_TYPE.DROPDOWN_MULTI: {
      const opts = correctionOptions(field);
      const selected = value ? value.split(MULTI_VALUE_SEPARATOR) : [];
      const otherEntry = selected.find(isOtherValue);
      const toggle = (o: string) => {
        const next = selected.includes(o) ? selected.filter((s) => s !== o) : [...selected, o];
        onChange(next.join(MULTI_VALUE_SEPARATOR));
      };
      const toggleOther = () =>
        onChange(
          (otherEntry ? selected.filter((s) => s !== otherEntry) : [...selected, OTHER_LABEL]).join(
            MULTI_VALUE_SEPARATOR,
          ),
        );
      // The separator cannot appear inside the free text.
      const setOtherText = (t: string) =>
        onChange(
          selected
            .map((s) =>
              s === otherEntry ? toOtherValue(t.split(MULTI_VALUE_SEPARATOR).join("|")) : s,
            )
            .join(MULTI_VALUE_SEPARATOR),
        );
      return (
        <fieldset
          style={{
            border: `1px solid ${errBorder}`,
            borderRadius: "6px",
            padding: "8px 10px",
            margin: 0,
          }}
        >
          <legend
            style={{ fontSize: "1.2rem", fontWeight: 600, color: errColor, padding: "0 4px" }}
          >
            Issue Category
            <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
          </legend>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {opts.map((o) => (
              <label
                key={o}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "6px",
                  padding: "3px 0",
                  fontSize: "1.3rem",
                  color: "#374151",
                  cursor: disabled ? "default" : "pointer",
                  lineHeight: 1.4,
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(o)}
                  disabled={disabled}
                  onChange={() => toggle(o)}
                  style={{ marginTop: "3px", flexShrink: 0 }}
                />
                <span style={{ wordBreak: "break-word" }}>{o}</span>
              </label>
            ))}
            {field.allow_other && (
              <OtherOption
                type="checkbox"
                checked={!!otherEntry}
                text={otherEntry ? otherText(otherEntry) : ""}
                disabled={disabled}
                hasError={hasError}
                onToggle={toggleOther}
                onText={setOtherText}
              />
            )}
          </div>
        </fieldset>
      );
    }

    // -- Numeric: number input with min/max/step --
    case FIELD_TYPE.NUMERIC:
      return (
        <div>
          <div
            style={{ fontSize: "1.2rem", fontWeight: 600, color: errColor, marginBottom: "4px" }}
          >
            Issue Category
            <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
          </div>
          <input
            type="number"
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter correct number"
            min={field.min ?? undefined}
            max={field.max ?? undefined}
            step={field.step ?? undefined}
            style={{
              ...S.formInput,
              fontSize: "1.4rem",
              borderColor: errBorder,
              width: "180px",
            }}
          />
        </div>
      );

    // -- Text / default: textarea --
    default:
      return (
        <div>
          <div
            style={{ fontSize: "1.2rem", fontWeight: 600, color: errColor, marginBottom: "4px" }}
          >
            Issue Category
            <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
          </div>
          <textarea
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Explain the evidence and the correct interpretation"
            rows={2}
            style={{
              ...S.formInput,
              fontSize: "1.4rem",
              borderColor: errBorder,
              resize: "vertical" as const,
              minHeight: "48px",
            }}
          />
        </div>
      );
  }
};

// ----------------------------  OtherOption  ----------------------------
// Built-in "Other" choice for allow_other dropdowns; reveals a free-text box when picked.

interface OtherOptionProps {
  type: "radio" | "checkbox";
  name?: string;
  checked: boolean;
  text: string;
  disabled?: boolean;
  hasError: boolean;
  onToggle: () => void;
  onText: (text: string) => void;
}

const OtherOption: React.FC<OtherOptionProps> = ({
  type,
  name,
  checked,
  text,
  disabled,
  hasError,
  onToggle,
  onText,
}) => (
  <div>
    <label style={optionRow(disabled)}>
      <input
        type={type}
        name={name}
        value={OTHER_LABEL}
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        style={{ marginTop: "3px", flexShrink: 0 }}
      />
      <span>{OTHER_LABEL}</span>
    </label>
    {checked && (
      <input
        type="text"
        value={text}
        disabled={disabled}
        autoFocus={!text}
        onChange={(e) => onText(e.target.value)}
        placeholder="Describe the other category"
        aria-label="Other category"
        style={{
          ...S.formInput,
          fontSize: "1.3rem",
          marginTop: "4px",
          borderColor: hasError && !text.trim() ? "#ef4444" : "#d1d5db",
        }}
      />
    )}
  </div>
);

const optionRow = (disabled?: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "6px",
  padding: "3px 0",
  fontSize: "1.3rem",
  color: "#374151",
  cursor: disabled ? "default" : "pointer",
  lineHeight: 1.4,
});

// ----------------------------  CheckboxValue  ----------------------------

const CheckboxValue: React.FC<{ label: string; checked: boolean }> = ({ label, checked }) => (
  <label
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "6px 0",
      cursor: "default",
      fontSize: "1.3rem",
      color: "#374151",
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled
      style={{ width: "26px", height: "26px", accentColor: "#0f7b8a" }}
    />
    {label}
  </label>
);

// ----------------------------  Styles  ----------------------------

const questionBanner: React.CSSProperties = {
  padding: "10px 14px",
  fontWeight: 400,
  fontSize: "1.3rem",
  color: "#1e3a5f",
  background: "#f0f7ff",
  borderRadius: "6px 6px 0 0",
  border: "1px solid #bfdbfe",
  borderBottom: "none",
};

const fieldCard: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: "6px",
  padding: "14px 16px",
  marginBottom: "8px",
  background: "white",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
};

const fieldHeader: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const fieldLabel: React.CSSProperties = {
  fontWeight: 600,
  fontSize: "1.4rem",
};

const valuesRow: React.CSSProperties = {
  display: "flex",
  gap: "12px",
};

const valueBlock: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const valueLabel: React.CSSProperties = {
  fontSize: "1.12rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#0f7b8a",
  marginBottom: "4px",
};

const valueBox: React.CSSProperties = {
  background: "#f0fdfa",
  border: "1px solid #ccfbf1",
  borderRadius: "6px",
  padding: "10px 12px",
  fontSize: "1.3rem",
  lineHeight: 1.6,
  color: "#1f2937",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: "300px",
  overflowY: "auto",
};

const decisionRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const decisionLabel: React.CSSProperties = {
  fontSize: "1.2rem",
  fontWeight: 600,
  color: "#6b7280",
  flexShrink: 0,
};

const pillLabel: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "5px 14px",
  borderRadius: "16px",
  fontSize: "1.3rem",
  fontWeight: 500,
  cursor: "pointer",
  transition: "all 0.15s",
  border: "1px solid transparent",
  userSelect: "none",
};

const pillDefault: React.CSSProperties = {
  background: "#f3f4f6",
  color: "#4b5563",
  borderColor: "#d1d5db",
};

const pillSelected: React.CSSProperties = {
  background: "#0f7b8a",
  color: "white",
  borderColor: "#0f7b8a",
};

const noteLabel: React.CSSProperties = {
  fontSize: "1.2rem",
  fontWeight: 600,
  color: "#6b7280",
  marginBottom: "4px",
};

const correctionSection: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  paddingTop: "4px",
  borderTop: "1px solid #f3f4f6",
};

export default FieldReviewTable;
